import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";

/** One person on a tab. */
export interface LiveTeamPerson {
  user_id: number;
  name: string;
  email: string;
}

/** One function on a tab, with what it does today. */
export interface LiveTeamFunction {
  key: string;
  label: string;
  module: string;
  module_label: string;
  /** post | review | blocked */
  policy: string;
  default: string;
  changed: boolean;
}

export interface LiveTeamTab {
  key: string;
  label: string;
  modules: string[];
  makers: LiveTeamPerson[];
  checkers: LiveTeamPerson[];
  functions: LiveTeamFunction[];
  counts: Record<string, number>;
  changed_count: number;
  gate?: {
    status: "ok" | "warn" | "block" | "empty";
    message: string | null;
    makers: number;
    checkers: number;
    reviews: number;
  };
}

export interface LiveTeamMember {
  user_id: number;
  name: string;
  email: string;
  /** The tenant role, e.g. `ZN Priya u42`. */
  role: string;
  is_zn: boolean;
  /** tab key → maker | checker */
  seats: Record<string, string>;
}

/** The tenant's board, exactly as it answered. */
export interface LiveTeamBoard {
  tabs: LiveTeamTab[];
  members: LiveTeamMember[];
  unseated: LiveTeamMember[];
  policies: string[];
  seats: string[];
  validation: {
    ok: boolean;
    blocks: { scope: string; tab: string | null; label: string | null; message: string }[];
    warnings: { scope: string; tab: string | null; label: string | null; message: string }[];
  };
}

export interface LiveTeamState {
  client: { public_id: string; name: string; status: string; live_at: string | null };
  company: { id: number; app_url: string } | null;
  team_board_url: string;
  board: LiveTeamBoard | null;
  /** What reading the tenant corrected in master's own copy. */
  refresh: {
    seats_written: number;
    seats_skipped: number;
    role_names_filled: number;
    assignments_created: number;
  } | null;
  /** Set instead of `board` when the tenant could not be read. */
  unreachable: string | null;
}

/**
 * The live client's team, read back from its tenant.
 *
 * Every call is also a repair: master's `onboarding_module_seats` are rewritten
 * from the tenant's seats and any blank `user_company_assignments.role_name` is
 * filled from the tenant's role, which is what "Enter Dashboard" gates on.
 */
export const getLiveTeam = async (companyId: number): Promise<LiveTeamState> => {
  const token = getToken();
  if (!token) throw new Error("No authentication token found");

  const response = await fetch(`${API_BASE_URL}/api/onboarding/live-team/state`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ company_id: companyId }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.message || `Could not read the team (HTTP ${response.status}).`);
  }

  return payload.data as LiveTeamState;
};
